import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import type { EventGuest } from '../../types/eventGuest';

interface EraseGuestDialogProps {
  open: boolean;
  guest: EventGuest;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Plan 4.6 — GDPR erase confirmation. Destructive Button stays disabled
 * until the user types the guest's first name (case-insensitive) to
 * reduce accidental erasures.
 */
const EraseGuestDialog: React.FC<EraseGuestDialogProps> = ({
  open,
  guest,
  onConfirm,
  onCancel,
}) => {
  const [typed, setTyped] = useState('');
  const matches =
    typed.trim().toLowerCase() === guest.firstName.trim().toLowerCase();

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        Permanently remove {guest.firstName}?
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This permanently removes {guest.firstName}'s personal details. We
          can't undo this. Past event registrations will show "[Removed]".
        </Alert>
        <DialogContentText sx={{ mb: 2 }}>
          To confirm, type <strong>{guest.firstName}</strong> below.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          label="Type the first name to confirm"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => {
            setTyped('');
            onCancel();
          }}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            setTyped('');
            onConfirm();
          }}
          color="error"
          variant="contained"
          disabled={!matches}
        >
          Erase permanently
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EraseGuestDialog;
